import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { BaseViewModel } from '../../../../../models/base/base-view-model';
import { RouteUtils } from '../../../../../utils/route-utils';
import type { NavItem } from '../models/nav-item';
import type { NavSubItem } from '../models/nav-sub-item';

@Injectable()
export class NavSubItemViewModel extends BaseViewModel {
  constructor(protected router: Router) {
    super();
  }

  private readonly _currentNavUrl = new BehaviorSubject<string>('');
  public readonly currentNavUrl$ = this._currentNavUrl.pipe(distinctUntilChanged());
  connectToCurrentNavUrl(url: string) {
    this._currentNavUrl.next(url);
  }

  private readonly _navItem = new BehaviorSubject<NavItem>(null);
  public readonly navItem$ = this._navItem as Observable<NavItem>;
  connectToNavItem(item: NavItem) {
    this._navItem.next(item);
  }

  private readonly _subItem = new BehaviorSubject<NavSubItem>(null);
  public readonly subItem$ = this._subItem as Observable<NavSubItem>;
  connectToSubNavItem(item: NavSubItem) {
    this._subItem.next(item);
  }

  public readonly isNavItemActive$ = combineLatest([this.navItem$, this.subItem$, this.currentNavUrl$]).pipe(
    tap(([nav, sub, url]) => {
      const urlWithoutFragment = RouteUtils.cleanFragments(url);
      const currentUrlExactlyMatchesSubNavUrl = sub?.buildFullRouterPath(nav) === urlWithoutFragment;
      if (currentUrlExactlyMatchesSubNavUrl) {
        const fragment = RouteUtils.getFragmentFromUrl(url);
        nav?.saveSubPosition(sub, fragment);
        sub?.saveSubFragment(fragment);
      }
    }),
    // This determines if the sub nav item should appear highlighted,
    // because the route is exact or a child of this sub nav item.
    map(([nav, sub, url]) => url?.indexOf(sub?.buildFullRouterPath(nav)) === 0),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
